import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash2, Edit2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "text", l: "Text" },
  { v: "number", l: "Number" },
  { v: "textarea", l: "Textarea" },
  { v: "file", l: "File upload" },
];
const TRANSFORMS = [
  { v: "", l: "No transform" },
  { v: "uppercase", l: "UPPERCASE" },
  { v: "lowercase", l: "lowercase" },
];

export default function LiveSupportForms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | form

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/live-support/forms");
      setForms(data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (f) => {
    if (!window.confirm(`Delete form "${f.title}"?`)) return;
    try {
      await api.delete(`/admin/live-support/forms/${f.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const reseed = async () => {
    if (!window.confirm("Delete all forms and restore BetJili-style defaults?")) return;
    try {
      await api.post("/admin/live-support/forms/reseed");
      toast.success("Defaults restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reseed failed"); }
  };

  return (
    <div data-testid="ls-forms-page">
      <PageHeader title="Live Support Forms" subtitle="Define data-capture forms that users fill when they pick a terminal topic (e.g. ডিপোজিট পেন্ডিং)." />

      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="text-sm text-gray-600">{forms.length} forms</div>
        <div className="flex items-center gap-2">
          <button onClick={reseed} data-testid="ls-form-reseed"
                  className="h-10 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-2">
            <RefreshCw size={13} /> Restore defaults
          </button>
          <button onClick={() => setEditing("new")} data-testid="ls-form-add"
                  className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Plus size={15} /> New Form
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-600 font-bold">
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Key</th>
              <th className="px-4 py-2.5">Fields</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading...</td></tr>
             : forms.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-gray-500">No forms yet</td></tr>
             : forms.map((f) => (
              <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">{f.title}</td>
                <td className="px-4 py-3 font-mono text-gray-700">{f.key}</td>
                <td className="px-4 py-3 text-gray-700">{(f.fields || []).length} field{(f.fields || []).length === 1 ? "" : "s"}</td>
                <td className="px-4 py-3">
                  {f.active
                    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">Active</span>
                    : <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 text-xs font-bold">Disabled</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(f)} className="h-8 px-2 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1 mr-1">
                    <Edit2 size={12} /> Edit
                  </button>
                  <button onClick={() => remove(f)} className="h-8 px-2 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold inline-flex items-center gap-1">
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
             ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <FormEditor
          form={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function FormEditor({ form, onClose, onSaved }) {
  const [key, setKey] = useState(form?.key || "");
  const [title, setTitle] = useState(form?.title || "");
  const [desc, setDesc] = useState(form?.description || "");
  const [submitLabel, setSubmitLabel] = useState(form?.submit_label || "সাবমিট করুন");
  const [successMsg, setSuccessMsg] = useState(form?.success_message || "✅ আপনার রিপোর্ট জমা হয়েছে।");
  const [active, setActive] = useState(form?.active ?? true);
  const [fields, setFields] = useState(form?.fields || []);
  const [saving, setSaving] = useState(false);

  const addField = () => setFields((fs) => [...fs, { key: `field_${fs.length + 1}`, label: "New Field", hint: "", type: "text", required: true, placeholder: "", transform: "", accept: "" }]);
  const updateField = (i, patch) => setFields((fs) => fs.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeField = (i) => setFields((fs) => fs.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    setFields((fs) => {
      const next = [...fs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return fs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim() || !title.trim()) { toast.error("Key + title required"); return; }
    if (fields.length === 0) { toast.error("Add at least one field"); return; }
    setSaving(true);
    const body = {
      key: key.trim(),
      title: title.trim(),
      description: desc,
      submit_label: submitLabel,
      success_message: successMsg,
      active,
      fields: fields.map((f) => ({
        key: f.key, label: f.label, hint: f.hint || "", type: f.type || "text",
        required: !!f.required, placeholder: f.placeholder || "",
        transform: f.transform || null,
        accept: f.accept || null,
      })),
    };
    try {
      if (form) await api.put(`/admin/live-support/forms/${form.id}`, body);
      else await api.post("/admin/live-support/forms", body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-sm shadow-xl w-full max-w-3xl my-8 p-5 flex flex-col max-h-[90vh]" data-testid="ls-form-editor">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="font-bold text-gray-900">{form ? "Edit Form" : "New Form"}</div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>

        <div className="overflow-y-auto pr-1 -mr-1 flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Key (unique, no spaces)</label>
              <input value={key} onChange={(e) => setKey(e.target.value.replace(/\s+/g, "_").toLowerCase())} required
                     className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono bg-white text-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Title (shown to user)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} required
                     className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Description (info block at top of form)</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Submit button label</label>
              <input value={submitLabel} onChange={(e) => setSubmitLabel(e.target.value)}
                     className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Success message</label>
              <input value={successMsg} onChange={(e) => setSuccessMsg(e.target.value)}
                     className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-3">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active</span>
          </label>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-gray-900">Fields ({fields.length})</div>
              <button type="button" onClick={addField} className="h-9 px-3 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1">
                <Plus size={12} /> Add Field
              </button>
            </div>

            {fields.length === 0 && <div className="text-sm text-gray-500 p-6 text-center border border-dashed border-gray-200 rounded">No fields yet — click &quot;Add Field&quot;</div>}

            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="border border-gray-200 rounded-sm p-3 bg-gray-50/40">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-500 font-bold">Field #{i + 1}</div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => move(i, -1)} className="h-7 w-7 rounded hover:bg-gray-200 text-gray-600 flex items-center justify-center" title="Up"><ChevronUp size={14} /></button>
                      <button type="button" onClick={() => move(i, 1)} className="h-7 w-7 rounded hover:bg-gray-200 text-gray-600 flex items-center justify-center" title="Down"><ChevronDown size={14} /></button>
                      <button type="button" onClick={() => removeField(i)} className="h-7 w-7 rounded hover:bg-red-100 text-red-600 flex items-center justify-center" title="Remove"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={f.key} onChange={(e) => updateField(i, { key: e.target.value.replace(/\s+/g, "_") })}
                           placeholder="Field key (e.g. transaction_id)" className="h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono bg-white text-gray-900" />
                    <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })}
                           placeholder="Label (shown to user)" className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })}
                            className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900">
                      {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <select value={f.transform || ""} onChange={(e) => updateField(i, { transform: e.target.value })}
                            disabled={f.type === "file"}
                            className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900 disabled:opacity-50">
                      {TRANSFORMS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer px-2">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                      <span>Required</span>
                    </label>
                  </div>
                  <div className="mt-2">
                    <input value={f.placeholder || ""} onChange={(e) => updateField(i, { placeholder: e.target.value })}
                           placeholder="Placeholder" className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
                  </div>
                  <div className="mt-2">
                    <input value={f.hint || ""} onChange={(e) => updateField(i, { hint: e.target.value })}
                           placeholder="Hint (small text shown below label)" className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
                  </div>
                  {f.type === "file" && (
                    <div className="mt-2">
                      <input value={f.accept || ""} onChange={(e) => updateField(i, { accept: e.target.value })}
                             placeholder="Accept (e.g. image/*,application/pdf)" className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono bg-white text-gray-900" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ls-form-save"
                  className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold">
            {saving ? "Saving..." : "Save Form"}
          </button>
        </div>
      </form>
    </div>
  );
}
